import * as Sentry from "@sentry/browser";

import Vector from "victor";
import Camera from "./Camera";
import { ShapeDOMRepresentation } from "./types/ShapeDOMRepresentation";
import {
  PenShapeDOMRepresentation,
  EraserShapeDOMRepresentation,
} from "./pen/PenShapeDOMRepresentation";
import { StandardShapeDOMRepresentation } from "./standardShapes/StandardShapeDOMRepresentations";
import TextShapeDOMRepresentation from "./text/TextShapeDOMRepresentation";
import { Shape2 } from "./types/Shape";
import { ShapeType } from "@/wbcontroller/types/ShapeType";
import WhiteboardControllerInterface from "./types/WhiteboardControllerInterface";
import { PenlikeShape2, EraserShape2 } from "./pen/PenShape";
import {
  SubjectMessageAny,
  SubjectMessageType,
  SubjectMessageUpsert,
} from "./ShapeStore";
import {
  LineShape2,
  RectShape2,
  DiamondShape2,
  EllipseShape2,
} from "./standardShapes/StandardShapes";

import { NodeDOMRepresentation } from "./NodeDOMRepresentation";
import Rect from "./types/Rect";
import { TextShape2 } from "./text/TextShape";
import KefirBus from "./KefirBus";
import { DrawingInterface } from "./DrawingInterface";
import { RenderConfig } from "@/store/modules/toolConfig";
import { HasImageURL } from "./types/attributes";

/**
 * Translates shape JSON objects to DOM
 */
export default class DrawingDOMRepresentation implements DrawingInterface {
  orderedShapes: ShapeDOMRepresentation[] = [];
  indexedShapes: Record<string, ShapeDOMRepresentation> = {};
  lastTouchedShapeID: string | null = null;
  nextZIndex = 0;

  focusedTextShapeIDBus = new KefirBus<string | null, void>(
    "focusedTextShapeID"
  );

  erasers: Record<string, EraserShape2> = {};

  private isLoaded = false;
  private isDead = false;

  finishLoad() {
    this.isLoaded = true;
    console.time("FirstRender");
    let i = 0;
    for (const s of this.orderedShapes) {
      i += 1;
      if (i % 500 === 0) {
        console.log(`${i}/${this.orderedShapes.length}`);
      }
      s.update(s.shape, true);
    }
    console.timeEnd("FirstRender");
    this._sort();
  }

  constructor(
    private wb: WhiteboardControllerInterface,
    public camera: Camera,
    public transformEl: HTMLDivElement,
    public renderConfig: RenderConfig
  ) {
    const changes = wb.shapeStore.changes.filter(() => !this.isDead);

    const deletes = changes.filter(
      (msg: { type: SubjectMessageType }) =>
        msg.type === SubjectMessageType.Delete
    );

    const upserts = changes
      .filter(
        (msg: { type: SubjectMessageType }) =>
          msg.type === SubjectMessageType.Upsert
      )
      .map((obj: unknown) => obj as SubjectMessageUpsert);

    const creates = upserts.filter(
      ({ subjectID }) => !this.indexedShapes[subjectID]
    );
    const updates = upserts.filter(
      ({ subjectID }) => !!this.indexedShapes[subjectID]
    );

    deletes.onValue((msg) => {
      this.deleteShape(msg.subjectID, this.isLoaded);
    });

    upserts.onValue((msg) => {
      if (
        msg.attributes.zIndex !== undefined &&
        msg.attributes.zIndex >= this.nextZIndex
      ) {
        this.nextZIndex = msg.attributes.zIndex + 1 + Math.random();
        this.wb.updateNextZIndex(this.nextZIndex);
      }
    });

    creates.onValue((msg) => {
      this._create(msg.subjectID, msg.attributes, this.isLoaded);
    });

    updates.onValue((msg) => {
      this._update(msg.subjectID, msg.attributes, this.isLoaded);
    });

    this.camera.scaleStream.onValue(this.occlude);
  }

  teardown() {
    this.isDead = true;
    this.camera.scaleStream.offValue(this.occlude);
    this.orderedShapes.forEach((s) => s.teardown());
  }

  private lastViewportRect: Rect | null = null;
  occlude = () => {
    if (this.lastViewportRect?.isEqualTo(this.camera.viewportRect) === true) {
      return;
    }
    this.lastViewportRect = this.camera.viewportRect;
    const expandedRect = this.camera.viewportRect.inset(
      this.camera.viewportRect.size.clone().multiplyScalar(-0.5)
    );
    for (const s of this.orderedShapes) {
      s.occlude(expandedRect);
    }
  };

  get focusedTextShapeID(): string | null {
    return this.focusedTextShapeIDBus.lastEmittedValue || null;
  }
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  focusTextShape(id: string | null, source: string): void {
    if (id && (this.indexedShapes[id]?.shape as HasImageURL).imageURL) {
      // Ignore text shapes that have image URLs because they are really nodes rendering images
      // yes this is a bad design, shut up
      this.focusedTextShapeIDBus.emit(null);
      return;
    }
    // console.log("Focus shape", id, "from", source);
    this.focusedTextShapeIDBus.emit(id);
  }

  private _sort() {
    // orderedShapes will already be sorted 90% of the time, but it's probably
    // fine to just sort it every time.
    this.orderedShapes.sort((a, b) => a.shape.zIndex - b.shape.zIndex);
  }

  private _add(id: string, s: ShapeDOMRepresentation, sortAndRedraw = true) {
    if (this.indexedShapes[id]) {
      Sentry.captureException(new Error("Trying to double-add a shape"), {
        extra: { shapeType: s.shape.shapeType },
      });
    }
    this.indexedShapes[id] = s;
    this.orderedShapes.push(s);
    const i = this.orderedShapes.length - 1;
    if (
      sortAndRedraw &&
      this.orderedShapes.length > 1 &&
      this.orderedShapes[i - 1].shape.zIndex >= s.shape.zIndex
    ) {
      this._sort();
    }
    if (sortAndRedraw) {
      s.occlude(this.camera.viewportRect);
    }
    this.lastTouchedShapeID = s.id;
  }

  private _create(id: string, shape: Shape2, isLoaded = true) {
    switch (shape.shapeType) {
      case ShapeType.Line:
        this._add(
          id,
          new StandardShapeDOMRepresentation(
            id,
            shape as LineShape2,
            this.transformEl,
            this.camera,
            this.renderConfig
          ),
          isLoaded
        );
        break;
      case ShapeType.Rect:
        this._add(
          id,
          new StandardShapeDOMRepresentation(
            id,
            shape as RectShape2,
            this.transformEl,
            this.camera,
            this.renderConfig
          ),
          isLoaded
        );
        break;
      case ShapeType.Diamond:
        this._add(
          id,
          new StandardShapeDOMRepresentation(
            id,
            shape as DiamondShape2,
            this.transformEl,
            this.camera,
            this.renderConfig
          ),
          isLoaded
        );
        break;
      case ShapeType.Ellipse:
        this._add(
          id,
          new StandardShapeDOMRepresentation(
            id,
            shape as EllipseShape2,
            this.transformEl,
            this.camera,
            this.renderConfig
          ),
          isLoaded
        );
        break;
      case ShapeType.Node:
        this._add(
          id,
          new NodeDOMRepresentation(
            this.wb,
            id,
            shape as EllipseShape2,
            this.transformEl,
            this.camera
          ),
          isLoaded
        );
        break;
      case "pen":
        this._add(
          id,
          new PenShapeDOMRepresentation(
            id,
            shape as PenlikeShape2,
            this.transformEl,
            this.camera
          ),
          isLoaded
        );
        break;
      case "eraser":
        if ((shape as PenlikeShape2).isOpen) {
          // Render eraser in progress as white line
          this._add(
            id,
            new EraserShapeDOMRepresentation(
              id,
              shape as PenlikeShape2,
              this.transformEl,
              this.camera
            ),
            isLoaded
          );
        } else {
          this.erasers[id] = shape as EraserShape2;
          // Render finished eraser to existing shapes, only if it hasn't moved
          for (const shapeDR of this.orderedShapes) {
            shapeDR.applyEraser(shape as EraserShape2, id, isLoaded);
          }
        }
        break;
      case "text":
        this._add(
          id,
          new TextShapeDOMRepresentation(
            this.wb,
            id,
            shape as TextShape2,
            this.transformEl,
            this.camera
          ),
          isLoaded
        );
        break;
    }
  }

  private _update(id: string, shape: Shape2, sortAndRedraw = true) {
    const lastZIndex = this.indexedShapes[id].shape.zIndex;
    this.indexedShapes[id].update(shape, sortAndRedraw);
    if (sortAndRedraw && lastZIndex !== shape.zIndex) {
      this._sort();
    }
  }

  applySubjectMessage(msg: SubjectMessageAny) {
    switch (msg.type) {
      case SubjectMessageType.Delete:
    }
  }

  deleteShape(id: string, redraw = true) {
    if (this.erasers[id]) {
      const shape = this.erasers[id];
      if (!shape) return;
      for (const shapeDR of this.orderedShapes) {
        shapeDR.removeEraser(shape as EraserShape2, id, redraw);
      }
    }

    this.orderedShapes = this.orderedShapes.filter((s) => s.id !== id);
    if (this.indexedShapes[id]) {
      this.indexedShapes[id].teardown();
      delete this.indexedShapes[id];
    }
  }

  // Helpers

  findBoundingRect(): Rect {
    if (this.orderedShapes.length <= 0) {
      return new Rect(new Vector(0, 0), new Vector(0, 0));
    }

    const result = new Rect(
      this.orderedShapes[0].boundingRect.min,
      this.orderedShapes[0].boundingRect.max
    );
    for (const shape of this.orderedShapes) {
      result.expand(shape.boundingRect.min);
      result.expand(shape.boundingRect.max);
    }
    return result;
  }
}
